// @ts-nocheck
import PropTypes from 'prop-types';
import { withApollo } from 'react-apollo';
import { useEffect, useMemo, useState } from 'react';

import { TIME_UP_LOCKED_TYPES } from 'Components/Classroom/Content/constant';
import { useClassroomCourseData } from 'Domain/Classroom';
import { getSubsectionInMode } from 'Util/Classroom/learningModeHelper';
import useIsClassroomTimeUp from 'Util/Hooks/useIsClassroomTimeUp';

import { fillSubSectionNumber } from '../../GraphQL/query/Classroom.query';
import { LEARNING_MODE, useClassroomContext } from '../../Context/ClassroomContext';

/**
 * Check if the subsection is the first or last of the section
 */
const validateSubsectionOrderInSection = (sections, currentSubsectionId, variant) => {
  return (
    sections.findIndex((section) => {
      const lastSubsection = section.subSections[section.subSections.length - 1];
      const firstSubsection = section.subSections[0];
      switch (variant) {
        case 'FIRST':
          return currentSubsectionId === firstSubsection?.id;
        case 'LAST':
        default:
          return currentSubsectionId === lastSubsection?.id;
      }
    }) !== -1
  );
};

function findCanPlayNextSubsectionByMode(data, currentSubsectionId, previousMode, nextMode) {
  let startIndex = -1;

  // Find the start index of the current subsection
  for (let i = 0; i < data.length; i++) {
    if (
      data[i].learningModes &&
      data[i].id === currentSubsectionId &&
      data[i].learningModes.includes(previousMode)
    ) {
      startIndex = i;
      break;
    }
  }

  if (startIndex === -1) {
    return null; // If the current subsection is not found
  }

  // Find the next subsection with the desired mode
  for (let i = startIndex + 1; i < data.length; i++) {
    if (data[i].learningModes && data[i].learningModes.includes(nextMode)) {
      return data[i];
    }
  }

  return null; // If no subsection with the desired mode is found
}

/**
 * @param {Subsection} subsection
 * @param {CourseData["learningModeCompletedSubsectionIds"]} learningModeCompletedSubsectionIds
 * @param {LearningMode} selectedLearningMode
 * @returns {{complete: boolean, completedLearningModeList: (learningModeCompletedSubsectionIds[subsection.referId] | [])}}
 */
const isSubSectionComplete = (
  subsection,
  learningModeCompletedSubsectionIds,
  selectedLearningMode
) => {
  const learningModeCompleted = learningModeCompletedSubsectionIds[subsection.referId];
  const learningModes = subsection.learningModes ?? [LEARNING_MODE.DEFAULT];
  const isInSelectedLearningMode = learningModes.includes(selectedLearningMode);

  /**
   * Note: for subsection.referId => referId will be appeared in learningModeCompletedSubsectionIds when each subsection is learned.
   * Then API will put the mode which complete learning into learningModeCompletedSubsectionIds
   * e.g. currentLearningMode is "DEFAULT" and user finish learning on current subsection
   * the result will be => cd9dae75-29ff-4630-83b9-e2cf10c385de: ["DEFAULT"]
   */
  if (!learningModeCompleted || !isInSelectedLearningMode) {
    return { complete: false, completedLearningModeList: [] };
  } else if (
    !learningModeCompleted.includes(LEARNING_MODE.DEFAULT) &&
    (subsection.contentType === 'QUIZ' || subsection.contentType === 'VIDEO')
  ) {
    return {
      complete: true,
      completedLearningModeList: learningModeCompleted,
    };
  } else {
    return {
      complete: true,
      completedLearningModeList: learningModes,
    };
  }
};

/**
 * This container component provides necessary functions/data for navigating among subsections in Classroom.
 * It needs to be used under ClassroomProvider.
 */
const ClassroomNavigation = ({ children }) => {
  const {
    updateCurrentSubsection,
    currentSubsectionId,
    courseData,
    hasClassroomSession,
    currentLearningMode,
  } = useClassroomContext();
  const { isClassroomTrialMode } = useClassroomCourseData();
  const isTimeUp = useIsClassroomTimeUp();
  const [previousMode, setPreviousMode] = useState();

  const {
    isSectionGroupActive = false,
    sectionGroups: originalSectionGroups = [],
    sections: originalSections = [],
    learningModeCompletedSubsectionIds = {},
    subsections: defaultSubsections = [],
  } = courseData ?? {};

  const sectionWithStatus = useMemo(() => {
    const sectionWithStatus = originalSections.map((section) => {
      const subSections = section.subSections.map((subsection) => ({
        ...subsection,
        ...isSubSectionComplete(
          subsection,
          learningModeCompletedSubsectionIds,
          currentLearningMode
        ),
        // failed: failedSubsectionIds.includes(subsection.referId),
        // submitted: submittedSubsectionIds.includes(subsection.referId),
        active: currentSubsectionId === subsection.id,
      }));
      const { isLock: isSectionLock, releasedAt } = subSections?.[0] ?? {};
      return {
        ...section,
        releasedAt: isSectionLock ? releasedAt : null,
        subSections,
      };
    });
    return sectionWithStatus;
  }, [
    learningModeCompletedSubsectionIds,
    currentLearningMode,
    currentSubsectionId,
    originalSections,
  ]);

  const sectionGroupWithStatus = useMemo(() => {
    const activeSectionGroupWithStatus = originalSectionGroups.map((sectionGroup) => {
      const sections = sectionGroup.sections.map((section) => {
        const subsections = section.subSections.map((subsection, index) => ({
          ...subsection,
          ...isSubSectionComplete(
            subsection,
            learningModeCompletedSubsectionIds,
            currentLearningMode
          ),
          // failed: failedSubsectionIds.includes(subsection.referId),
          // submitted: submittedSubsectionIds.includes(subsection.referId),
          active: currentSubsectionId === subsection.id,
        }));
        const { isLock: isSectionLock, releasedAt } = subsections?.[0] ?? {};
        return {
          ...section,
          active: subsections.some((subsection) => subsection.active),
          releasedAt: isSectionLock ? releasedAt : null,
          subsections,
        };
      });

      const sectionsFilledSubsectionNumber =
        /**@type {FillSubSectionNumberReturnType<typeof sections>}*/ (
          fillSubSectionNumber(sections)
        );
      return {
        ...sectionGroup,
        sections: sectionsFilledSubsectionNumber,
      };
    });
    return activeSectionGroupWithStatus;
  }, [
    originalSectionGroups,
    currentLearningMode,
    learningModeCompletedSubsectionIds,
    currentSubsectionId,
  ]);

  /** @type {FlattedSubsection[]} */
  const subsectionsInMode = useMemo(
    () => getSubsectionInMode(defaultSubsections, currentLearningMode ?? LEARNING_MODE.DEFAULT),
    [currentLearningMode, defaultSubsections]
  );

  const currentSubsectionDefaultIndex = defaultSubsections.findIndex(
    ({ id }) => id === currentSubsectionId
  );

  useEffect(() => {
    setPreviousMode(currentLearningMode);
  }, [currentLearningMode]);

  useEffect(() => {
    const currentSubsectionInModeIndex = subsectionsInMode.findIndex(
      ({ id }) => id === currentSubsectionId
    );

    if (subsectionsInMode.length > 0 && defaultSubsections.length > 0) {
      const lastIndexSubsectionLearningMode = subsectionsInMode.length - 1;
      const nextSubsectionLearningMode = findCanPlayNextSubsectionByMode(
        defaultSubsections,
        currentSubsectionId,
        previousMode,
        currentLearningMode
      );

      if (currentSubsectionInModeIndex >= 0) {
        const currentSubsectionLearningMode = subsectionsInMode[currentSubsectionInModeIndex];
        updateCurrentSubsection(
          currentSubsectionLearningMode.id,
          currentSubsectionLearningMode.referId
        );
      } else if (nextSubsectionLearningMode) {
        updateCurrentSubsection(nextSubsectionLearningMode.id, nextSubsectionLearningMode.referId);
      } else {
        const lastSubsectionLearningMode = subsectionsInMode[lastIndexSubsectionLearningMode];
        updateCurrentSubsection(lastSubsectionLearningMode.id, lastSubsectionLearningMode.referId);
      }
    }
  }, [currentSubsectionDefaultIndex, currentSubsectionId, currentLearningMode]);

  if (!courseData || !hasClassroomSession) return children({});

  // Once learning time runs out, next/previous skip the content that would only show the time-up modal
  const reachableSubsectionsInMode = subsectionsInMode.filter(
    ({ contentType }) => !(isTimeUp && TIME_UP_LOCKED_TYPES.includes(contentType))
  );

  let /**@type {?FlattedSubsection=}*/ nextSubsection,
    /**@type {?FlattedSubsection=}*/ previousSubsection,
    /**@type {?FlattedSubsection=}*/ currentSubsection,
    /**@type {boolean}*/ isLastSubsectionInSection,
    /**@type {boolean}*/ isFirstSubsectionInSection;

  currentSubsection =
    currentSubsectionDefaultIndex >= 0 ? defaultSubsections[currentSubsectionDefaultIndex] : null;

  if (isClassroomTrialMode) {
    const previewableSections = originalSections.map((section) => {
      return {
        ...section,
        subSections: section.subSections.filter(({ canPreview }) => canPreview),
      };
    });

    const previewableSubsectionsInMode = reachableSubsectionsInMode.filter(
      ({ canPreview }) => canPreview
    );

    /**
     * Specify whether if currentSubsection is the first or last in any sections.
     */
    isFirstSubsectionInSection = validateSubsectionOrderInSection(
      previewableSections,
      currentSubsectionId,
      'FIRST'
    );

    isLastSubsectionInSection = validateSubsectionOrderInSection(
      previewableSections,
      currentSubsectionId,
      'LAST'
    );

    nextSubsection = defaultSubsections
      .slice(currentSubsectionDefaultIndex + 1, defaultSubsections.length)
      .find(
        ({ id: nextId }) =>
          previewableSubsectionsInMode.findIndex((subsection) => subsection.id === nextId) >= 0
      );

    previousSubsection =
      currentSubsectionDefaultIndex >= 0
        ? defaultSubsections
            .slice(0, currentSubsectionDefaultIndex)
            .reverse()
            .find(
              ({ id }) =>
                previewableSubsectionsInMode.findIndex((subsection) => subsection.id === id) >= 0
            )
        : null;
  } else {
    /**
     * Specify whether if currentSubsection is the first or last in any sections.
     */
    isFirstSubsectionInSection = validateSubsectionOrderInSection(
      sectionWithStatus,
      currentSubsectionId,
      'FIRST'
    );
    isLastSubsectionInSection = validateSubsectionOrderInSection(
      sectionWithStatus,
      currentSubsectionId,
      'LAST'
    );
    /**
     * An object contain data of next/previous subsection including
     * { id, referId, title, contentType }
     */
    nextSubsection = defaultSubsections
      .slice(currentSubsectionDefaultIndex + 1, defaultSubsections.length)
      .find(
        ({ id: nextId }) =>
          reachableSubsectionsInMode.findIndex((subsection) => subsection.id === nextId) >= 0
      );

    previousSubsection =
      currentSubsectionDefaultIndex >= 0
        ? defaultSubsections
            .slice(0, currentSubsectionDefaultIndex)
            .reverse()
            .find(
              ({ id: prevId }) =>
                reachableSubsectionsInMode.findIndex((subsection) => subsection.id === prevId) >= 0
            )
        : null;
  }

  return children({
    isSectionGroupActive,
    sectionGroups: sectionGroupWithStatus,
    sections: fillSubSectionNumber(sectionWithStatus),
    isLastSubsectionInCourse: !nextSubsection,
    isLastSubsectionInSection,
    isFirstSubsectionInCourse: !previousSubsection,
    isFirstSubsectionInSection,
    goToNextSubsection: () => {
      if (!nextSubsection) return;
      updateCurrentSubsection(nextSubsection.id, nextSubsection.referId);
    },
    goToPreviousSubsection: () => {
      if (!previousSubsection) return;
      updateCurrentSubsection(previousSubsection.id, previousSubsection.referId);
    },
    goToSubsection: updateCurrentSubsection,
    nextSubsection,
    previousSubsection,
    currentSubsection,
  });
};

ClassroomNavigation.propTypes = {
  children: PropTypes.func,
};

ClassroomNavigation.defaultProps = {
  children: () => {},
};

export default withApollo(ClassroomNavigation);

/**
 * @typedef {import('Context/ClassroomContext/context').FlattedSubsection} FlattedSubsection
 * @typedef {import('Context/ClassroomContext/context').Subsection} Subsection
 * @typedef {import('Context/ClassroomContext/context').CourseData} CourseData
 * @typedef {import('Context/ClassroomContext/context').LearningMode} LearningMode
 */
/**
 * @template T
 * @typedef {{sections: { subSections: { type: string; number: number; contentLength: unknown }[] }[]} & T } FillSubSectionNumberReturnType<T>
 */
